/**
 * Helper functions for useSearchCache hook
 */

import { CacheEntry, SearchResponse } from '../types/search';

/**
 * Generate cache key with normalization
 * Ensures consistent caching for similar queries
 */
export const normalizeKey = (key: string): string => {
  return key.toLowerCase().trim().replace(/\s+/g, ' ');
};

/**
 * Check if cache entry is expired
 */
export const isExpired = (entry: CacheEntry<SearchResponse>): boolean => {
  return Date.now() > entry.expiresAt;
};

/**
 * Update access order for LRU policy
 */
export const updateAccessOrder = (key: string, accessOrder: string[]): void => {
  const existingIndex = accessOrder.indexOf(key);
  
  if (existingIndex > -1) {
    // Move to end (most recently used)
    accessOrder.splice(existingIndex, 1);
  }
  
  accessOrder.push(key);
};

/**
 * Evict least recently used entries if cache is full
 */
export const evictIfNeeded = (
  cache: Map<string, CacheEntry<SearchResponse>>,
  accessOrder: string[],
  maxSize: number
): void => {
  while (cache.size >= maxSize && accessOrder.length > 0) {
    const lruKey = accessOrder.shift();
    if (lruKey) {
      cache.delete(lruKey);
    }
  }
};

/**
 * Remove key from access order
 */
export const removeFromAccessOrder = (key: string, accessOrder: string[]): void => {
  const index = accessOrder.indexOf(key);
  if (index > -1) {
    accessOrder.splice(index, 1);
  }
};

/**
 * Check if cache entry is stale but still usable
 */
export const checkIfStale = (
  entry: CacheEntry<SearchResponse> | undefined,
  ttl: number
): boolean => {
  if (!entry) return false;
  
  const staleTtl = ttl * 0.8; // Consider stale at 80% of TTL
  const age = Date.now() - entry.timestamp;
  
  return age > staleTtl && !isExpired(entry);
};

/**
 * Handle cache miss
 */
export const handleCacheMiss = (
  normalizedKey: string,
  cache: Map<string, CacheEntry<SearchResponse>>,
  accessOrder: string[],
  stats: { hits: number; misses: number }
): null => {
  cache.delete(normalizedKey);
  removeFromAccessOrder(normalizedKey, accessOrder);
  stats.misses++;
  return null;
};

/**
 * Handle cache hit
 */
export const handleCacheHit = (
  entry: CacheEntry<SearchResponse>,
  normalizedKey: string,
  accessOrder: string[],
  stats: { hits: number; misses: number }
): SearchResponse => {
  entry.accessCount++;
  updateAccessOrder(normalizedKey, accessOrder);
  stats.hits++;
  return entry.data;
};

/**
 * Create new cache entry
 */
export const createCacheEntry = (
  data: SearchResponse,
  ttl: number
): CacheEntry<SearchResponse> => {
  const now = Date.now();
  return {
    data,
    timestamp: now,
    expiresAt: now + ttl,
    accessCount: 1,
  };
};